/**
 * Servicio para gestión de compras de productos/jornadas
 * 
 * Este servicio maneja todas las operaciones relacionadas con las compras:
 * - Obtener compras del usuario
 * - Consultar jornadas disponibles
 * - Gestionar uso de sesiones
 * - Estadísticas de compras (admin)
 */

import api from './api';

const ENDPOINTS = {
  // Endpoints para usuarios
  USER_PURCHASES: '/purchases',
  ACTIVE_PURCHASES: '/purchases/active',
  SESSIONS_SUMMARY: '/purchases/sessions-summary',
  IS_NEW_FUTBOLISTA: (futbolistaId) => `/purchases/is-new-futbolista/${futbolistaId}`,
  JORNADAS_INSCRITAS: (futbolistaId) => `/purchases/jornadas-inscritas/${futbolistaId}`,
  VERIFICAR_EQUIPACION: '/purchases/verificar-equipacion',
  PURCHASE_BY_ID: (id) => `/purchases/${id}`,
  USE_SESSIONS: (id) => `/purchases/${id}/use-sessions`,

  // Endpoints administrativos
  ADMIN_ALL_PURCHASES: '/purchases/admin/all',
  ADMIN_PURCHASE_STATS: '/purchases/admin/stats',
  ADMIN_ASSIGN_MANUAL: '/purchases/admin/assign-manual'
};

const purchaseService = {
  /**
   * Obtener compras del usuario autenticado
   */
  getUserPurchases: async (params = {}) => {
    try {
      const { status, page = 1, limit = 10 } = params;
      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString()
      });
      
      if (status) queryParams.append('status', status);
      
      const response = await api.get(`${ENDPOINTS.USER_PURCHASES}?${queryParams}`);
      return response.data;
    } catch (error) {
      console.error('❌ Error al obtener compras del usuario:', error);
      throw error;
    }
  },

  /**
   * Obtener compras activas del usuario
   */
  getActivePurchases: async () => {
    try {
      const response = await api.get(ENDPOINTS.ACTIVE_PURCHASES);
      return response.data;
    } catch (error) {
      console.error('❌ Error al obtener compras activas:', error);
      throw error;
    }
  },

  /**
   * Obtener resumen de sesiones del usuario
   */
  getSessionsSummary: async () => {
    try {
      const response = await api.get(ENDPOINTS.SESSIONS_SUMMARY);
      return response.data;
    } catch (error) {
      console.error('❌ Error al obtener resumen de sesiones:', error);
      throw error;
    }
  },

  /**
   * Verificar si un futbolista es nuevo (sin compras previas)
   * @param {number} futbolistaId - ID del futbolista en PostgreSQL
   */
  isNewFutbolista: async (futbolistaId) => {
    try {
      const response = await api.get(ENDPOINTS.IS_NEW_FUTBOLISTA(futbolistaId));
      return response.data;
    } catch (error) {
      console.error(`❌ Error al verificar si futbolista ${futbolistaId} es nuevo:`, error);
      throw error;
    }
  },

  /**
   * Obtener IDs de jornadas donde el futbolista ya está inscrito (activo) para un torneo
   */
  getJornadasInscritas: async (futbolistaId, torneoId) => {
    try {
      const params = torneoId ? { torneo_id: torneoId } : {};
      const response = await api.get(ENDPOINTS.JORNADAS_INSCRITAS(futbolistaId), { params });
      return response.data?.data || [];
    } catch (error) {
      console.error(`❌ Error al obtener jornadas inscritas para ${futbolistaId}:`, error);
      return [];
    }
  },

  /**
   * Verificar si la equipación es obligatoria para paquetes específicos
   */
  verificarEquipacionObligatoria: async (futbolistaId, paquetes) => {
    try {
      const response = await api.post(ENDPOINTS.VERIFICAR_EQUIPACION, {
        futbolistaId,
        paquetes
      });
      return response.data;
    } catch (error) {
      console.error(`❌ Error al verificar equipacion obligatoria para ${futbolistaId}:`, error);
      throw error;
    }
  },

  /**
   * Obtener detalles de una compra específica
   */
  getPurchaseById: async (purchaseId) => {
    try {
      const response = await api.get(ENDPOINTS.PURCHASE_BY_ID(purchaseId));
      return response.data;
    } catch (error) {
      console.error('❌ Error al obtener detalles de compra:', error);
      throw error;
    }
  },

  /**
   * Usar sesiones de una compra (para futura implementación)
   */
  useSessions: async (purchaseId, sessionsData) => {
    try {
      const response = await api.post(ENDPOINTS.USE_SESSIONS(purchaseId), sessionsData);
      return response.data;
    } catch (error) {
      console.error('❌ Error al usar sesiones:', error);
      throw error;
    }
  },

  /**
   * Obtener todas las compras (Admin)
   */
  getAllPurchases: async (params = {}) => {
    try {
      const {
        status,
        userId,
        productId,
        paymentMethod,
        page = 1,
        limit = 20,
        sortBy = 'purchaseDate',
        sortOrder = 'desc'
      } = params;

      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        sortBy,
        sortOrder
      });

      if (status) queryParams.append('status', status);
      if (userId) queryParams.append('userId', userId);
      if (productId) queryParams.append('productId', productId);
      if (paymentMethod) queryParams.append('paymentMethod', paymentMethod);

      const response = await api.get(`${ENDPOINTS.ADMIN_ALL_PURCHASES}?${queryParams}`);
      return response.data;
    } catch (error) {
      console.error('❌ Error al obtener todas las compras:', error);
      throw error;
    }
  },

  /**
   * Obtener estadísticas de compras (Admin)
   */
  getPurchaseStats: async (params = {}) => {
    try {
      const { startDate, endDate } = params;
      const queryParams = new URLSearchParams();

      if (startDate) queryParams.append('startDate', startDate);
      if (endDate) queryParams.append('endDate', endDate);

      const response = await api.get(`${ENDPOINTS.ADMIN_PURCHASE_STATS}?${queryParams}`);
      return response.data;
    } catch (error) {
      console.error('❌ Error al obtener estadísticas de compras:', error);
      throw error;
    }
  },

  /**
   * Asignar producto manualmente a un usuario (Admin)
   */
  assignProductManually: async (assignmentData) => {
    try {
      const {
        userId,
        productId,
        originalPaymentMethod,
        assignmentReason,
        notes
      } = assignmentData;

      // Validaciones básicas
      if (!userId || !productId || !originalPaymentMethod || !assignmentReason) {
        throw new Error('Todos los campos obligatorios deben ser proporcionados');
      }

      const response = await api.post(ENDPOINTS.ADMIN_ASSIGN_MANUAL, {
        userId,
        productId,
        originalPaymentMethod,
        assignmentReason,
        notes
      });

      return response.data;
    } catch (error) {
      console.error('❌ Error al asignar producto manualmente:', error);
      throw error;
    }
  },

  /**
   * Formatear estado de compra para mostrar
   */
  formatPurchaseStatus: (status) => {
    const statusMap = {
      active: { text: 'Activa', color: 'success' },
      expired: { text: 'Expirada', color: 'warning' },
      fully_used: { text: 'Agotada', color: 'secondary' },
      cancelled: { text: 'Cancelada', color: 'danger' }
    };
    return statusMap[status] || { text: status, color: 'secondary' };
  },

  /**
   * Formatear método de pago para mostrar
   */
  formatPaymentMethod: (method) => {
    const methodMap = {
      redsys: 'Tarjeta (Redsys)',
      wallet: 'Monedero',
      transfer: 'Transferencia',
      cash: 'Efectivo',
      manual: 'Asignación Manual'
    };
    return methodMap[method] || method;
  },

  /**
   * Formatear método de pago original para asignaciones manuales
   */
  formatOriginalPaymentMethod: (method) => {
    const methodMap = {
      card: 'Tarjeta/Bizum',
      bizum: 'Tarjeta/Bizum',
      transfer: 'Transferencia',
      cash: 'Efectivo',
      other: 'Otro'
    };
    return methodMap[method] || method;
  },

  /**
   * Calcular progreso de uso de sesiones
   */
  calculateUsageProgress: (purchase) => {
    if (!purchase.totalSessions || purchase.totalSessions === 0) return 0;
    return Math.round((purchase.usedSessions / purchase.totalSessions) * 100);
  },

  /**
   * Verificar si una compra está próxima a expirar
   */
  isNearExpiration: (purchase, daysThreshold = 7) => {
    if (!purchase.expirationDate) return false;
    
    const expirationDate = new Date(purchase.expirationDate);
    const now = new Date();
    const diffTime = expirationDate - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays <= daysThreshold && diffDays > 0;
  },

  /**
   * Obtener días restantes hasta expiración
   */
  getDaysUntilExpiration: (purchase) => {
    if (!purchase.expirationDate) return null;
    
    const expirationDate = new Date(purchase.expirationDate);
    const now = new Date();
    const diffTime = expirationDate - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays;
  },

  /**
   * Formatear fecha de compra
   */
  formatPurchaseDate: (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  },

  /**
   * Formatear precio
   */
  formatPrice: (price, currency = 'EUR') => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: currency
    }).format(price);
  },

  /**
   * Obtener icono para el estado de la compra
   */
  getStatusIcon: (status) => {
    const iconMap = {
      active: 'check_circle',
      expired: 'schedule',
      fully_used: 'done_all',
      cancelled: 'cancel'
    };
    return iconMap[status] || 'help';
  },

  /**
   * Obtener color para el estado de la compra
   */
  getStatusColor: (status) => {
    const colorMap = {
      active: '#4caf50',
      expired: '#ff9800',
      fully_used: '#9e9e9e',
      cancelled: '#f44336'
    };
    return colorMap[status] || '#9e9e9e';
  }
};

export default purchaseService;
